import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  AiAgent,
  AiAgentOrderByInput,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { TableLayoutPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import {
  getSortDirectionByOrder,
  SorterData,
  TableSortData,
} from 'src/components/general/table-helpers';
import { TableLayoutPageContainer } from 'src/components/layout/table-layout-page-container';
import { AiAgentsTable } from 'src/components/pages/ai-agents/agents/components/ai-agents-table';
import { AI_AGENTS_LIST_QUERY } from 'src/components/pages/ai-agents/agents/queries';
import { AiAgentsListLayout } from 'src/components/pages/ai-agents/components/ai-agents-list-layout';
import {
  AiAgentsListVariables,
  useAiAgentsListQueryParams,
} from 'src/components/pages/ai-agents/hooks/use-ai-agents-list-query-params';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const DEFAULT_SORT_DATA: TableSortData = { key: 'name', direction: 'ASC' };

const resolveOrderBy = (sortData: TableSortData): AiAgentOrderByInput =>
  sortData.key === 'createdBy'
    ? { createdBy: { firstName: sortData.direction } }
    : { [sortData.key]: sortData.direction };

type AiAgentsListPageProps = {
  sectionUrl: string;
};

export const AiAgentsListPage = memo<AiAgentsListPageProps>(({ sectionUrl }) => {
  const { t } = useTranslation(['aiAgents']);
  const { handleNextPage, handlePreviousPage, handleSortChange, sortData, variables } =
    useAiAgentsListQueryParams({
      defaultSortData: DEFAULT_SORT_DATA,
      resolveOrderBy,
    });

  const handleGraphQLError = useHandleGraphQLError();

  const { data, loading } = useQuery<
    Pick<Query, 'aiAgents'>,
    AiAgentsListVariables<AiAgentOrderByInput>
  >(AI_AGENTS_LIST_QUERY, {
    variables,
    onError: handleGraphQLError,
  });

  if (!data || loading) {
    return (
      <TableLayoutPageContainer>
        <TableLayoutPlaceholder />
      </TableLayoutPageContainer>
    );
  }

  const { nodes, totalCount, pageInfo } = data.aiAgents.listAIAgents;

  return (
    <AiAgentsListLayout
      testId="ai-agents-page"
      totalCount={totalCount}
      entityName={t('aiAgents:pages.agents.count', { count: totalCount })}
      pageInfo={pageInfo}
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
    >
      <AiAgentsTable
        data={nodes}
        sortData={sortData}
        sectionUrl={sectionUrl}
        onChange={(pagination, filters, sorter) => {
          const { columnKey, order } = sorter as SorterData<AiAgent>;

          handleSortChange({ key: columnKey, direction: getSortDirectionByOrder(order) });
        }}
      />
    </AiAgentsListLayout>
  );
});
